import { type ApiFailure, classifyFailure, type FailureKind } from "@lafayette/api-client";

// The one mapping from the failure shape of the API clients to the words of the staff console (CHT-1992). An area (customers, payments,
// requests, team, catalog, imports, ...) calls `describeStaffFailure(error, words)` and gives only the words of its OWN refusals, its own
// refusal of access and the words that differ for a real reason. The status rules (which status is a conflict, a retry, a reload) are in
// `classifyFailure` of `@lafayette/api-client`, and they are tested once, at the transport, with a fake fetch.
//
// The words that differ for a real reason are explicit, named, per failure kind: an area gives `on: { <kind>: words }`. A slot may also
// name its own `kind` (Catalog tells "not_found" from "forbidden"), its own `retryable` (Imports does not retry a 500, Privacy does not
// retry a 429) and its own `reload` (Privacy and Operations read again after a 404).
//
// `failure-words.golden.test.ts` pins the answer of every area for every status and every refusal code to what it was before this module
// existed, so a changed word, kind or retry rule shows as a red test.
//
// Rules of every message:
//   1. No id, key, amount, name, reason or provider text goes into a message. The API sends none either: a refusal holds a code.
//   2. A refusal for access (403) and a missing id (404) read the same: the page never says whether a record exists.
//   3. A failure that may have run says so, and whether the SAME action may be sent again (`retryable`: the same Idempotency-Key makes
//      the server run it once).

export type StaffFailureKind =
  | "conflict"
  | "invalid"
  | "bad_request"
  | "forbidden"
  | "unauthenticated"
  | "rate_limited"
  | "unavailable"
  | "network"
  | "response"
  | "unknown";

export type StaffFailure<K extends string = StaffFailureKind> = Readonly<{
  kind: K;
  message: string;
  /** The same action may be sent again with the SAME idempotency key: it may or may not have run. */
  retryable: boolean;
  /** The page must read again: what the user saw is out of date. */
  reload: boolean;
  requestId: string | null;
  /** The shape the words were chosen from: the area reads its own refusal in `api.detail`. */
  api: ApiFailure;
}>;

/** The words of one failure, and the parts of the rule that this area does differently. Anything left out takes the shared answer. */
export type StaffSlot<K extends string = StaffFailureKind> = Readonly<{
  message: string;
  kind?: K;
  retryable?: boolean;
  reload?: boolean;
}>;

type Words<K extends string> =
  | string
  | StaffSlot<K>
  | ((failure: ApiFailure) => string | StaffSlot<K>);

export type StaffFailureWords<K extends string = StaffFailureKind> = Readonly<{
  /** 403 and 404 (read the same). See `neutralForbidden`. */
  forbidden: string;
  /** 409. Reads the area's own refusal in `failure.detail`. The page reloads. */
  conflict: Words<K>;
  /** 422. Default: `STAFF_WORDS.invalid`. */
  invalid?: Words<K>;
  /** 503. Default: `STAFF_WORDS.unavailable`. */
  unavailable?: Words<K>;
  /** A file that could not be sent to storage. Reads as `network`. Default: the words of `network`. */
  upload?: string;
  /** Words (and rules) of one failure kind that differ for a real reason. They win over every default above. */
  on?: Partial<Record<FailureKind, Words<K>>>;
}>;

export const STAFF_WORDS = {
  unauthenticated: "Your session has ended. Sign in again.",
  badRequest: "The request was not valid. Reload the page and try again.",
  invalid: "The server did not accept this. Check the fields and try again.",
  rateLimited: "Too many requests. Wait a moment and try again.",
  unavailable: "The service is not available right now. Try again in a moment.",
  /** 500, 502 and 504: the command may have run, and the same key is safe to send again. */
  server: "The server could not complete the request. Try again in a moment.",
  /** Any other status. */
  otherStatus: "The server could not complete the request.",
  network: "The server could not be reached. Check the connection and try again.",
  response: "The server answered with data this console does not understand. Reload the page.",
  other: "Something went wrong. Try again.",
} as const;

/** The neutral words of a refusal of access. One sentence, so the words never tell whether the record exists. */
export const neutralForbidden = (subject = "this", whoToAsk = "a supervisor"): string =>
  `You cannot open or change ${subject}. If you think you should have access, ask ${whoToAsk}.`;

/** The neutral refusal words of a default subject, for the areas that read a refusal as one sentence. */
export const NEUTRAL_FORBIDDEN_MESSAGE = neutralForbidden();

/** The kind (of the staff kinds) and the words that the shared rule gives to a failure kind, before an area changes them. */
function sharedSlot<K extends string>(
  api: ApiFailure,
  words: StaffFailureWords<K>,
): { kind: StaffFailureKind; words: Words<K> } {
  switch (api.kind) {
    case "conflict":
      return { kind: "conflict", words: words.conflict };
    case "invalid":
      return { kind: "invalid", words: words.invalid ?? STAFF_WORDS.invalid };
    case "bad_request":
      return { kind: "bad_request", words: STAFF_WORDS.badRequest };
    case "unauthenticated":
      return { kind: "unauthenticated", words: STAFF_WORDS.unauthenticated };
    case "forbidden":
    case "not_found":
      return { kind: "forbidden", words: words.forbidden };
    case "rate_limited":
      return { kind: "rate_limited", words: STAFF_WORDS.rateLimited };
    case "unavailable":
      return { kind: "unavailable", words: words.unavailable ?? STAFF_WORDS.unavailable };
    case "server":
      return { kind: "unknown", words: STAFF_WORDS.server };
    case "timeout":
    case "too_large":
    case "other_status":
      return { kind: "unknown", words: STAFF_WORDS.otherStatus };
    case "network":
      return { kind: "network", words: STAFF_WORDS.network };
    case "upload":
      return { kind: "network", words: words.upload ?? STAFF_WORDS.network };
    case "response":
      return { kind: "response", words: STAFF_WORDS.response };
    case "other":
      return { kind: "unknown", words: STAFF_WORDS.other };
  }
}

const slotOf = <K extends string>(spec: Words<K>, api: ApiFailure): StaffSlot<K> => {
  const resolved = typeof spec === "function" ? spec(api) : spec;
  return typeof resolved === "string" ? { message: resolved } : resolved;
};

/**
 * Words for a failure. Without `on`, a 400 is `bad_request` and a 422 is `invalid`; a 403 and a 404 are `forbidden`; a server failure that
 * may have run (500, 502, 504) is `unknown` and retryable. The area's union of kinds `K` must hold every kind that its words can reach.
 */
export function describeStaffFailure<K extends string = StaffFailureKind>(
  error: unknown,
  words: StaffFailureWords<K>,
): StaffFailure<K> {
  const api = classifyFailure(error);
  const shared = sharedSlot(api, words);
  const slot = slotOf(words.on?.[api.kind] ?? shared.words, api);
  return {
    kind: slot.kind ?? (shared.kind as K),
    message: slot.message,
    retryable: slot.retryable ?? api.retryable,
    reload: slot.reload ?? api.reload,
    requestId: api.requestId,
    api,
  };
}
